/**
 * InlineEdit Component
 *
 * A click-to-edit text component for inline editing.
 * Shows text normally, transforms to input on click.
 * Saves on Enter or blur, cancels on Escape.
 *
 * @example
 * ```tsx
 * <InlineEdit
 *   value="Current text"
 *   onSave={(newValue) => handleSave(newValue)}
 *   placeholder="Enter text..."
 * />
 * ```
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Pencil, Check, X } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

export interface InlineEditProps {
  /** Current value to display */
  value: string;
  /** Callback when value is saved */
  onSave: (newValue: string) => void;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for the text display */
  textClassName?: string;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Maximum length for input */
  maxLength?: number;
  /** Whether to show the edit icon on hover */
  showEditIcon?: boolean;
  /** Custom validation function */
  validate?: (value: string) => string | null;
}

/**
 * InlineEdit - Click-to-edit text component
 *
 * Features:
 * - Click text to enter edit mode
 * - Press Enter or blur to save
 * - Press Escape to cancel
 * - Optional validation
 * - Optional character limit
 */
export const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onSave,
  placeholder = 'Click to edit...',
  className = '',
  textClassName = '',
  disabled = false,
  maxLength,
  showEditIcon = true,
  validate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync with external value changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = useCallback(() => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(value);
    setError(null);
  }, [disabled, value]);

  const handleSave = useCallback(() => {
    const trimmedValue = editValue.trim();

    // Validation
    if (validate) {
      const validationError = validate(trimmedValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // Don't save if value hasn't changed
    if (trimmedValue === value) {
      setIsEditing(false);
      return;
    }

    onSave(trimmedValue);
    setIsEditing(false);
    setError(null);
  }, [editValue, value, onSave, validate]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue(value);
    setError(null);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Don't save on blur if clicking the save/cancel buttons
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (relatedTarget?.closest('[data-inline-edit-action]')) {
        return;
      }
      handleSave();
    },
    [handleSave]
  );

  if (isEditing) {
    return (
      <div className={cn('relative', className)}>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            maxLength={maxLength}
            placeholder={placeholder}
            className={cn(
              'flex-1 px-2 py-1 bg-dark-surface border rounded text-dark-text text-sm',
              'focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none',
              error ? 'border-red-500' : 'border-dark-border/30'
            )}
            data-testid="inline-edit-input"
          />
          <button
            type="button"
            onClick={handleSave}
            className="p-1 text-green-400 hover:text-green-300 hover:bg-dark-surface/50 rounded transition-all duration-150"
            data-inline-edit-action="save"
            title="Save (Enter)"
          >
            <Check size={14} />
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1 text-red-400 hover:text-red-300 hover:bg-dark-surface/50 rounded transition-all duration-150"
            data-inline-edit-action="cancel"
            title="Cancel (Escape)"
          >
            <X size={14} />
          </button>
        </div>
        {error && (
          <p className="absolute -bottom-5 left-0 text-xs text-red-400">{error}</p>
        )}
        {maxLength && (
          <p className="absolute -bottom-5 right-0 text-xs text-dark-muted">
            {editValue.length}/{maxLength}
          </p>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleStartEdit}
      disabled={disabled}
      className={cn(
        'group flex items-center gap-2 text-left rounded',
        'hover:bg-dark-surface/50 transition-all duration-150',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      title={disabled ? undefined : 'Click to edit'}
    >
      <span
        className={cn(
          'text-dark-text',
          !value && 'text-dark-muted italic',
          textClassName
        )}
      >
        {value || placeholder}
      </span>
      {showEditIcon && !disabled && (
        <Pencil
          size={12}
          className="opacity-0 group-hover:opacity-100 text-dark-muted transition-opacity"
        />
      )}
    </button>
  );
};

export default InlineEdit;
